/**
 * Capacitor Bridge
 * 
 * Platform detection and native utilities for Capacitor Android app.
 * All Capacitor imports are dynamic to avoid breaking the web build.
 */

import { Capacitor } from '@capacitor/core';

/**
 * Check if the app is running as a native Capacitor app (not web browser)
 */
export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

/**
 * Get the current platform: 'web', 'android', or 'ios'
 */
export function getPlatform() {
  return Capacitor.getPlatform();
}

/**
 * Open a URL in the native browser (Chrome Custom Tab on Android).
 * Falls back to window.location.href on web.
 * 
 * @param {string} url - The URL to open
 */
export async function openExternalUrl(url) {
  if (isNativeApp()) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } else {
    window.location.href = url;
  }
}

/**
 * Close the external browser opened by openExternalUrl.
 * No-op on web.
 */
export async function closeExternalBrowser() {
  if (isNativeApp()) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.close();
  }
}

/**
 * Register a deep link listener that fires when the app is opened via a URL.
 * Returns a cleanup function. No-op on web.
 * 
 * @param {function} callback - Called with the URL string when app is opened via deep link
 * @returns {function} cleanup - Call to remove listener
 */
export function addDeepLinkListener(callback) {
  if (!isNativeApp()) {
    return () => {}; // no-op cleanup
  }

  let listenerHandle = null;

  import('@capacitor/app').then(({ App }) => {
    App.addListener('appUrlOpen', ({ url }) => {
      callback(url);
    }).then(handle => {
      listenerHandle = handle;
    });
  });

  return () => {
    if (listenerHandle) {
      listenerHandle.remove();
    }
  };
}

/**
 * Register a back button listener for Android hardware back button.
 * Returns a cleanup function. No-op on web/iOS.
 * 
 * @param {function} callback - Called when hardware back button is pressed.
 *   Receives { canGoBack: boolean }. Call callback to handle it.
 * @returns {function} cleanup - Call to remove listener
 */
export function addBackButtonListener(callback) {
  if (!isNativeApp() || getPlatform() !== 'android') {
    return () => {};
  }

  let listenerHandle = null;

  import('@capacitor/app').then(({ App }) => {
    App.addListener('backButton', callback).then(handle => {
      listenerHandle = handle;
    });
  });

  return () => {
    if (listenerHandle) {
      listenerHandle.remove();
    }
  };
}

/**
 * Exit the native app. No-op on web.
 */
export async function exitApp() {
  if (isNativeApp()) {
    const { App } = await import('@capacitor/app');
    await App.exitApp();
  }
}

/**
 * Initialize the status bar for Android.
 * Sets dark content with the brand background color.
 */
export async function initStatusBar() {
  if (!isNativeApp()) return;

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#0D0C07' });
  } catch (e) {
    // Status bar plugin may not be available on all platforms
    console.warn('StatusBar init failed:', e);
  }
}

/**
 * Hide the splash screen. Called after the React app has mounted.
 */
export async function hideSplashScreen() {
  if (!isNativeApp()) return;

  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) {
    console.warn('SplashScreen hide failed:', e);
  }
}
